import { Box } from "@mui/material";
import {
  ModalProps,
  createUseModal,
  useFormModal,
} from "@imminently/react/context/modals";
import { Text } from "@imminently/react/ui/core/text";

import { HighriskIcon } from "@/assets/icons";
import MultilineTextInput from "@/components/MultilineTextInput";
import PatientCardModal from "@/components/PatientCardModal";
import { PatientEncounter } from "@/resources/types";
import { encounterService, peopleService } from "@/services";
import { css } from "@/theme";
import { LoadingButton } from '@mui/lab';

const { useUpdate } = peopleService;

const styles = {
  headIcon: css`
    text-align: center;
    display: block;
  `,
  underIcon: css`
    text-align: center;
    display: block;
    margin-top: 1rem;
    margin-bottom: 1rem;
    padding: 0 4.5rem;
  `,
};

interface DeanonymisePatientModalProps {
  encounter: PatientEncounter;
}

const DeanonymisePatientModal = ({ props }: ModalProps<DeanonymisePatientModalProps>) => {
  const { encounter } = props;
  // also detect anonymous patients by masked names since atm. the backend deletes the anonymous flag
  const isPatientAnonymous = (encounter.patient?.anonymous) || (encounter.patient?.firstName === "****" && encounter.patient?.lastName === "****");

  const { mutateAsync, isLoading } = useUpdate();
  const { methods, notify, close } = useFormModal({
    mode: "all",
    defaultValues: {
      deanonymiseNote: "",
    },
  });

  const { control, handleSubmit, formState } = methods;

  const submit = async (data: any) => {
    const deanonymiseNote = { text: data.deanonymiseNote };
    await mutateAsync({
      id: encounter.patient.id,
      payload: { anonymous: !isPatientAnonymous, deanonymiseNote },
    }).catch(() => {
      notify.error(`Failed to ${isPatientAnonymous ? "de-anonymise" : "anonymise"} patient`);
    });

    // Invalidate encounter queries to refresh patient data on the page
    await encounterService.invalideAllQueries();

    close(true);
  };

  return (
    <PatientCardModal
      title={isPatientAnonymous ? "De-anonymise Patient Record" : "Anonymise Patient Record"}
      encounter={encounter}
      onClose={() => close()}
    >
      <Box display="flex" alignItems="center" css={styles.headIcon}>
        <HighriskIcon />
      </Box>
      <Box css={styles.underIcon}>
        <Text variant="h4">
          Are you sure you want to {isPatientAnonymous ? "de-anonymise" : "anonymise"} the following patient record?
        </Text>
      </Box>

      <MultilineTextInput
        control={control}
        field="deanonymiseNote"
        required
        label={`Reason for ${isPatientAnonymous ? "de-anonymisation" : "anonymisation"}...`}
        rows={3}
      />

      <LoadingButton
        sx={{ ml: "auto" }}
        loading={isLoading}
        variant='contained'
        disabled={isLoading || !formState.isValid}
        onClick={handleSubmit(submit)}
      >
        Yes, {isPatientAnonymous ? "de-anonymise" : "anonymise"}
      </LoadingButton>
    </PatientCardModal>
  );
};

export const useDeanonymisePatientModal = createUseModal(DeanonymisePatientModal);
